<script lang="ts">
  import { createEventDispatcher, getContext, hasContext, tick } from 'svelte';
  import Menu from './Menu.svelte';
  import MenuItemRadio from './MenuItemRadio.svelte';
  import IconButton from '../IconButton.svelte';
  import type { FocusItem, FocusLayer } from '../../focus-manager/focus-types';
  import type { MenuContext } from '../../types/menu';

  /**
   * A label for the button. Will be used for the menu as well, if not set
   * explicitly.
   */
  export let label: string;

  /** A label for the menu. If omitted, the label prop will be used. */
  export let menuLabel: string | undefined = undefined;

  /**
   * An array of menu items.
   *
   * The type of the items can be any value. This value is passed in the
   * `selectItem` event triggered on selection of this menu item.
   */
  export let menuItems: Array<unknown>;

  /**
   * The selected menu item. Should be one of the items of {@link menuItems},
   * but does not have to.
   */
  export let selectedMenuItem: unknown;

  /**
   * An optional text to show in a bubble of the button. Can be used to
   * conveniently display the selected menu item.
   */
  export let bubbleText: string | undefined = undefined;

  /**
   * A formatter for the menu items. As the menu items can be of any value, you
   * can use this function to generate a string representation of the items.
   *
   * It will be called for each menu item when the menu is rendered.
   */
  export let formatMenuItem: (item: unknown) => string = (item) => String(item);

  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  let menuId: string;
  let isMenuOpen: boolean;
  let dispatch = createEventDispatcher();
  let wrapperElement: HTMLElement | undefined;

  let setMenuOpen = (_isOpen: boolean) => {
    //
    // to allow parent components to use the MenuButton without requiring a context, we provide an empty default implementation
  };

  if (hasContext('menu')) {
    ({ setMenuOpen } = getContext<MenuContext>('menu'));
  }

  let focusMenuButton: (() => void) | undefined;

  const openMenu = () => {
    isMenuOpen = true;
    setMenuOpen(true);
  };

  const closeMenu = async (shouldFocusButton: boolean = true) => {
    if (isMenuOpen) {
      isMenuOpen = false;
      setMenuOpen(false);

      if (shouldFocusButton) {
        await tick();
        if (focusMenuButton) {
          focusMenuButton();
        }
      }
    }
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const selectItem = (event: CustomEvent) => {
    dispatch('selectItem', event.detail);
    closeMenu();
  };

  const handleMenuClose = () => {
    closeMenu();
  };

  // We want to make sure that only one menu is open. So when another menu
  // opens, we have to close the previous one.
  // Additionally, we want to close the menu when the user clicks outside. But
  // we have to be careful when the user clicks on the button. That click is
  // outside the menu, but it should not toggle the menu twice (focusout on the
  // menu would close it, click on the button would open it again).
  //
  // So we listen on a focusout event on the wrapper. If an event happens and
  // the related target (that is, the element that receives focus) is outside
  // the wrapper, we close the menu.
  //
  // A focusout event happens whenever a new element receives focus. In contrast
  // to the blur event, it does bubble. So when the user navigates through the
  // menu items with the arrow keys, a focusout event happens on the previous
  // menu item and bubbles up to the wrapper element. The menu should stay open.
  // When the user uses the tab key to focus the next focusable element (which
  // is outside this MenuButton component), a focusout event also happens, and
  // this time we want to close the menu.
  // That's why we have to check whether the relatedTarget lies outside this
  // component.
  //
  // There's a bug in Safari where the relatedTarget is null even when it should
  // be the element receiving focus. This results in the menu not being closed
  // when clicking the menu button. Precisely: It falsely gets closed on
  // focusout and immediately gets opened again on click. Since it can be closed
  // by selecting a menu item or clicking anywhere else apart from the button,
  // we accept the bug.
  const closeMenuOnBlur = (event: FocusEvent) => {
    if (
      isMenuOpen &&
      wrapperElement &&
      !(event.relatedTarget && wrapperElement.contains(event.relatedTarget as HTMLElement))
    ) {
      closeMenu(false);
    }
  };
</script>

<div class="button-with-menu" on:focusout={closeMenuOnBlur} bind:this={wrapperElement}>
  <IconButton
    {...$$restProps}
    id={menuId}
    title={label}
    aria-label={label}
    aria-haspopup="true"
    aria-expanded={isMenuOpen}
    aria-controls={menuId}
    bind:focus={focusMenuButton}
    on:click={toggleMenu}
    {focusLayer}
    {focusName}
  >
    <slot />

    {#if bubbleText}
      <span class="button-label">{bubbleText}</span>
    {/if}
  </IconButton>

  <Menu
    id={menuId}
    on:close={handleMenuClose}
    open={isMenuOpen}
    label={menuLabel || label}
    anchorElement={wrapperElement}
  >
    {#each menuItems as menuItem}
      <MenuItemRadio
        value={menuItem}
        checked={menuItem === selectedMenuItem}
        on:selectItem={selectItem}
        {focusLayer}
        focusName={focusName + 'Option'}
      >
        {formatMenuItem(menuItem)}
      </MenuItemRadio>
    {/each}
  </Menu>
</div>

<style module>
  .button-with-menu {
    position: relative;
  }

  .button-with-menu :global(button) {
    position: relative;
    font-size: inherit;
  }

  .button-label {
    position: absolute;
    font-size: 0.7em;
    bottom: 6px;
    width: 70%;
    left: 50%;
    transform: translate(-50%);
    font-family: inherit;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    background-color: #f5f5f5;
    color: #424447;
    box-shadow: 0 -0.1em 0.2em currentColor;
  }

  .button-with-menu :global(button[aria-expanded='true']) {
    color: #ffffff; /* Fallback for older browsers */
    color: var(--focus-color);
    background: #000000;
    background: var(--focus-background);
  }
</style>
